import React, { useEffect, useRef, useState } from 'react';
import { Input } from '../../../../core/design-system/components/Input';
import { useLoadScript } from '@react-google-maps/api';
import './LocationInput.css';
import { GOOGLE_MAPS_CONFIG } from '../../../../config/googleMaps';
import { logger } from '../../../../shared/utils/logger';

const LocationInput = ({ value, onChange, placeholder, error: formError, onBlur }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const isInitializedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  
  // N'afficher l'erreur que si elle est explicitement passée par le parent
  // (généralement lors de la soumission du formulaire)
  const showError = !!formError;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_CONFIG.apiKey,
    libraries: GOOGLE_MAPS_CONFIG.libraries
  });

  // Fonction pour ajuster la position du conteneur de suggestions
  const fixSuggestionContainerPosition = () => {
    setTimeout(() => {
      const pacContainers = document.querySelectorAll('.pac-container');
      if (!pacContainers.length || !inputRef.current) return;
      
      const container = pacContainers[0];
      const inputRect = inputRef.current.getBoundingClientRect();
      
      // Forcer le positionnement sous le champ sur tous les appareils
      const parent = inputRef.current.closest('.location-input-wrapper');
      if (parent) {
        // Positionnement absolu par rapport au parent
        container.style.position = 'absolute';
        container.style.width = '100%';
        container.style.left = '0';
        container.style.top = `${inputRect.height}px`;
      }
    }, 50);
  };

  useEffect(() => {
    if (!isLoaded || !inputRef.current || isInitializedRef.current) return;
    
    isInitializedRef.current = true;

    try {
      // Configuration de l'Autocomplete
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          ...GOOGLE_MAPS_CONFIG.options,
          fields: ['formatted_address', 'geometry', 'place_id', 'name', 'address_components'],
          types: ['establishment', 'geocode']
        }
      );

      // Observer l'ajout de la liste de suggestions au DOM
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (!mutation.addedNodes?.length) continue;
          
          for (const node of mutation.addedNodes) {
            if (node.classList?.contains('pac-container')) {
              // Déplacer le conteneur de suggestions à l'intérieur du wrapper pour positionnement relatif
              const wrapper = inputRef.current.closest('.location-input-wrapper');
              if (wrapper && node.parentElement !== wrapper) {
                wrapper.appendChild(node);
              }
              fixSuggestionContainerPosition();
            }
          }
        }
      });
      
      observer.observe(document.body, { childList: true, subtree: true });

      // Empêcher la soumission du formulaire sur sélection Enter
      inputRef.current.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && autocompleteRef.current?.getPlace?.() === undefined) {
          e.preventDefault();
        }
      });

      // Gérer la sélection d'une adresse
      const placeChangedListener = autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();

        if (!place.geometry) {
          setSelectedAddress('');
          onChangeRef.current({
            address: inputRef.current.value,
            placeId: null,
            location: null
          });
          return;
        }

        const locationData = {
          name: place.name || null, // Nom du POI/établissement si disponible
          address: place.formatted_address || inputRef.current.value,
          placeId: place.place_id,
          location: place.geometry ? {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          } : null
        };

        // Afficher le nom si disponible (POI), sinon l'adresse
        const displayText = place.name || place.formatted_address || inputRef.current.value;
        setSelectedAddress(displayText);
        onChangeRef.current(locationData);
        setIsFocused(false);
        
        // Masquer les suggestions après sélection
        const pacContainers = document.querySelectorAll('.pac-container');
        if (pacContainers.length) {
          pacContainers[0].style.display = 'none';
        }
      });

      return () => {
        if (placeChangedListener) {
          window.google.maps.event.removeListener(placeChangedListener);
        }
        observer.disconnect();
      };

    } catch (error) {
      logger.error('Erreur lors de l\'initialisation de l\'autocomplétion:', error);
    }
  }, [isLoaded]);

  // Quand la valeur change de l'extérieur
  useEffect(() => {
    if (value?.address && value?.address !== selectedAddress) {
      // Afficher le nom si disponible, sinon l'adresse
      const displayText = value?.name || value?.address;
      setSelectedAddress(displayText);
    }
  }, [value?.address, value?.name, selectedAddress]);

  // Gestion focus/blur
  const handleInputFocus = () => {
    setIsFocused(true);
    setTimeout(fixSuggestionContainerPosition, 50);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setIsFocused(false);
      if (onBlur) onBlur();
    }, 200);
  };

  // Corriger la position lors des changements d'écran
  useEffect(() => {
    if (!isFocused) return;
    
    const handleResize = () => fixSuggestionContainerPosition();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isFocused]);

  if (loadError) {
    return (
      <div className="location-input-wrapper error">
        <Input
          type="text"
          value={value?.name || value?.address || ''}
          onChange={(e) => onChange({ ...value, address: e.target.value })}
          placeholder="Erreur de chargement Google Maps"
          className="location-input"
          disabled
        />
        {showError && <div className="error-message">{formError}</div>}
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="location-input-wrapper">
        <Input
          type="text"
          value={value?.name || value?.address || ''}
          placeholder="Chargement..."
          className="location-input"
          disabled
        />
      </div>
    );
  }

  // Déterminer si on doit afficher une classe d'erreur
  const hasError = showError;

  return (
    <div className={`location-input-wrapper ${hasError ? 'error' : ''}`}>
      <Input
        ref={inputRef}
        type="text"
        value={selectedAddress || ''}
        onChange={(e) => {
          const inputValue = e.target.value;
          setSelectedAddress(inputValue);
          
          // Si l'utilisateur tape, on perd la sélection POI
          const newValue = {
            name: inputValue === selectedAddress && value?.name ? value.name : null,
            address: inputValue,
            placeId: inputValue === selectedAddress ? value?.placeId : null,
            location: inputValue === selectedAddress ? value?.location : null
          };
          onChangeRef.current(newValue);
          
          if (isFocused) {
            fixSuggestionContainerPosition();
          }
        }}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        className="location-input"
        autoComplete="off"
      />
      {showError && formError && <div className="error-message">{formError}</div>}
    </div>
  );
};

export default LocationInput;